import { StreamMessageEvents } from '@/entities/ai';
import { i18n } from '@/shared/i18n';
import type { StreamEventGroup, SupportedSseTaskEvents } from './AgentTask.utils.types';

export type { StreamEventGroup, SupportedSseTaskEvents } from './AgentTask.utils.types';

// Type aliases for better readability
type CallId = string;

export const isSupportedSseTaskEvent = (
  event: StreamMessageEvents,
): event is SupportedSseTaskEvents => {
  return (
    event.type === 'data-agentStart' ||
    event.type === 'data-agentComplete' ||
    event.type === 'data-agentError' ||
    event.type === 'data-toolStart' ||
    event.type === 'data-toolComplete' ||
    event.type === 'data-toolError'
  );
};

/**
 * Organizes SSE events into a hierarchical structure with O(n) complexity
 */
export function groupSseEventsByHierarchy(events: SupportedSseTaskEvents[]): StreamEventGroup[] {
  if (events.length === 0) return [];

  // Build lookup maps for O(1) access
  const eventsByCallId = new Map<CallId, SupportedSseTaskEvents>();
  const childrenByParentId = new Map<CallId, SupportedSseTaskEvents[]>();
  const processedIds = new Set<CallId>();

  // Single pass to build lookup maps
  events.forEach((content) => {
    const callId = getCallId(content);
    const parentId = getParentCallId(content);

    if (callId) {
      eventsByCallId.set(callId, content);
    }

    if (parentId) {
      if (!childrenByParentId.has(parentId)) {
        childrenByParentId.set(parentId, []);
      }
      childrenByParentId.get(parentId)!.push(content);
    }
  });

  // Find root events (no parent)
  const rootEvents = events.filter((event) => !getParentCallId(event));

  // Build hierarchy for each root
  return rootEvents.map((rootEvent) =>
    buildStreamEventGroup(rootEvent, eventsByCallId, childrenByParentId, processedIds, 0),
  );
}

/**
 * Recursively builds an SSE event group and its children
 */
function buildStreamEventGroup(
  content: SupportedSseTaskEvents,
  eventsByCallId: Map<CallId, SupportedSseTaskEvents>,
  childrenByParentId: Map<CallId, SupportedSseTaskEvents[]>,
  processedIds: Set<CallId>,
  level: number,
): StreamEventGroup {
  const callId = getCallId(content);

  if (callId && processedIds.has(callId)) {
    // Return existing group to prevent cycles
    return {
      root: content,
      children: [],
      callId,
      level,
    };
  }

  if (callId) {
    processedIds.add(callId);
  }

  // Get children for this event
  const childEvents = callId ? childrenByParentId.get(callId) || [] : [];
  const children = childEvents.map((childEvent) =>
    buildStreamEventGroup(childEvent, eventsByCallId, childrenByParentId, processedIds, level + 1),
  );

  return {
    root: content,
    children,
    callId,
    level,
  };
}

/**
 * Extract callId from SSE event
 */
function getCallId(event: StreamMessageEvents): string | undefined {
  switch (event.type) {
    case 'data-toolStart':
    case 'data-toolComplete':
    case 'data-toolError':
      return event.data.callId;
    case 'data-agentStart':
    case 'data-agentComplete':
    case 'data-agentError':
      return event.data.agentId;
    case 'data-guardrailStart':
    case 'data-guardrailComplete':
      return event.data.agentId;
    default:
      return undefined;
  }
}

/**
 * Extract parent callId from SSE event
 */
function getParentCallId(event: StreamMessageEvents): string | undefined {
  switch (event.type) {
    case 'data-toolStart':
    case 'data-toolComplete':
    case 'data-toolError':
      return event.data.parentCallId;
    default:
      return undefined;
  }
}

/**
 * Get display name for SSE event
 */
export function getSseEventDisplayName(event: StreamMessageEvents): string {
  switch (event.type) {
    case 'data-agentStart':
    case 'data-agentComplete':
    case 'data-agentError':
      return event.data.agentName;
    case 'data-toolStart':
    case 'data-toolComplete':
    case 'data-toolError':
      return event.data.toolName;
    case 'data-guardrailStart':
    case 'data-guardrailComplete':
      return event.data.guardrailName;
    default:
      return i18n.t('chats:chats.agentTaskUtils.unknownEvent');
  }
}

/**
 * Get description for SSE event
 */
export function getSseEventDescription(event: StreamMessageEvents): string {
  switch (event.type) {
    case 'data-toolStart':
    case 'data-toolComplete':
    case 'data-toolError':
      return event.data.toolDescription || '';
    case 'data-agentStart':
    case 'data-agentComplete':
    case 'data-agentError':
      return i18n.t('chats:chats.agentTaskUtils.agent', { name: event.data.agentName });
    case 'data-guardrailStart':
    case 'data-guardrailComplete':
      return i18n.t('chats:chats.agentTaskUtils.guardrail', { name: event.data.guardrailName });
    default:
      return '';
  }
}
